import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, RefreshCw, MessageSquare, Mail, Bell, Send, CheckCircle, XCircle, AlertTriangle, Activity, QrCode, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import { istDateTime } from './utils/istDate';

const API_BASE_URL = '/api';

const WHATSAPP_VARIABLES = [
  { code: '{name}', label: 'Customer Name' },
  { code: '{date}', label: 'Booking Date' },
  { code: '{time}', label: 'Time Slot' },
  { code: '{amount}', label: 'Total Amount' },
  { code: '{id}', label: 'Booking ID' },
  { code: '{type}', label: 'Photoshoot / Event' },
  { code: '{phone}', label: 'Customer Phone' },
  { code: '{email}', label: 'Customer Email' },
  { code: '{duration}', label: 'Duration' },
];

const EMAIL_VARIABLES = [
  { code: '{name}', label: 'Customer Name' },
  { code: '{type}', label: 'Booking Type' },
  { code: '{date}', label: 'Booking Date' },
  { code: '{time}', label: 'Time Slot' },
  { code: '{amount}', label: 'Total Amount' },
];

// ─── Reusable textarea field ───────────────────────────────────────────────
const TemplateField = ({ label, value, onChange, rows = 4, hint }) => (
  <div className="space-y-1.5">
    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">{label}</label>
    {hint && <p className="text-xs text-gray-400">{hint}</p>}
    <div className="relative group">
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl text-sm font-medium text-gray-700 focus:border-[#471F21] focus:ring-0 outline-none transition-all shadow-sm font-mono leading-relaxed resize-y"
      />
      <div className="absolute inset-x-0 bottom-0 h-1 bg-[#471F21] scale-x-0 group-focus-within:scale-x-100 transition-transform origin-left rounded-b-2xl" />
    </div>
  </div>
);

// ─── Single-line input ─────────────────────────────────────────────────────
const SubjectField = ({ value, onChange }) => (
  <div className="space-y-1.5">
    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Email Subject</label>
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full px-5 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-medium text-gray-700 focus:border-[#471F21] focus:ring-0 outline-none transition-all shadow-sm"
    />
  </div>
);

// ─── Variables reference bar ───────────────────────────────────────────────
const VariablesBar = ({ variables }) => (
  <div className="bg-gray-50/50 p-5 rounded-2xl border border-gray-100">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-1 h-3 bg-[#471F21] rounded-full" />
      <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Available Placeholders</h4>
    </div>
    <div className="flex flex-wrap gap-3">
      {variables.map(v => (
        <div key={v.code} className="flex items-center gap-1.5">
          <code className="text-[10px] font-bold bg-[#471F21]/5 text-[#471F21] px-1.5 py-0.5 rounded cursor-help">
            {v.code}
          </code>
          <span className="text-[10px] font-medium text-gray-500">{v.label}</span>
        </div>
      ))}
    </div>
  </div>
);

// ─── Section card wrapper ──────────────────────────────────────────────────
const SectionCard = ({ title, badge, children, onReset, resetLabel }) => (
  <div className="border border-gray-100 rounded-2xl overflow-hidden">
    <div className="flex items-center justify-between px-6 py-4 bg-gray-50/50 border-b border-gray-100">
      <div className="flex items-center gap-3">
        <span className="font-semibold text-gray-800">{title}</span>
        {badge && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#471F21]/10 text-[#471F21] uppercase tracking-wider">
            {badge}
          </span>
        )}
      </div>
      {onReset && (
        <button
          onClick={onReset}
          className="text-[10px] font-bold text-blue-500 hover:text-blue-700 uppercase tracking-wider transition-colors"
        >
          {resetLabel || 'Reset to Default'}
        </button>
      )}
    </div>
    <div className="p-6 space-y-4">{children}</div>
  </div>
);

// ─── Main component ────────────────────────────────────────────────────────
const SettingsNotifications = () => {
  const [channel, setChannel] = useState('whatsapp');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [wa, setWa] = useState({
    booking_confirmation: '',
    booking_cancellation: '',
    booking_reminder: '',
    booking_caretaker: '',
    cancellation_caretaker: '',
    day_before_customer: '',
    day_before_caretaker: '',
    hour_before_customer: '',
    hour_before_caretaker: '',
  });

  const [email, setEmail] = useState({
    booking_confirmation: { subject: '', greeting: '', message: '' },
    booking_cancellation: { subject: '', message: '' },
  });

  // Where caretaker alerts and caretaker reminders go. Empty falls back to the
  // CARETAKER_PHONE env var on the server.
  const [caretakerPhone, setCaretakerPhone] = useState('');
  const [reminders, setReminders] = useState({
    day_before: { enabled: true, send_at: '09:00' },
    hour_before: { enabled: true, hours_before: 1 },
  });

  // ── WhatsApp test & status ──
  const [waStatus, setWaStatus] = useState(null);
  const [waStatusLoading, setWaStatusLoading] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [testSending, setTestSending] = useState(false);
  // ── QR pairing (inline, so re-linking never needs the service's Basic-auth password) ──
  const [qrPanelOpen, setQrPanelOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);

  const token = localStorage.getItem('admin_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchSettings(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWaStatus = async () => {
    try {
      setWaStatusLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/admin/whatsapp/status`, { headers });
      setWaStatus(data);
    } catch (err) {
      setWaStatus({ success: false, whatsapp_ready: false, error: err.message });
    } finally {
      setWaStatusLoading(false);
    }
  };

  useEffect(() => {
    if (channel === 'test') fetchWaStatus();
  }, [channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchQr = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/admin/whatsapp/qr`, { headers });
      setQrData(data);
      // Pairing finished — drop the QR panel and refresh the status card so the newly
      // linked number shows up.
      if (data?.ready) {
        setQrPanelOpen(false);
        fetchWaStatus();
        toast.success('WhatsApp connected!');
      }
    } catch (err) {
      setQrData({ success: false, error: err.response?.data?.error || err.message });
    }
  };

  // Codes expire in roughly 20s, so poll while the panel is open rather than showing one
  // frozen image — scanning an expired code fails silently, with nothing on the phone to
  // say why.
  useEffect(() => {
    if (!qrPanelOpen) return;
    fetchQr();
    const id = setInterval(fetchQr, 5000);
    return () => clearInterval(id);
  }, [qrPanelOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Codes last about 20s. The service re-loads WhatsApp Web when they stop refreshing,
  // so a stale reading here is temporary — but it must never look scannable.
  const qrAgeMs = qrData?.generated_at ? Date.now() - new Date(qrData.generated_at).getTime() : 0;
  const qrIsStale = qrData?.has_qr && qrAgeMs > 25000;

  const logoutWhatsApp = async () => {
    if (!window.confirm(
      'Log out the connected WhatsApp number?\n\nNo messages can be sent until a new number is linked by scanning a QR code.'
    )) return;
    try {
      setLoggingOut(true);
      const { data } = await axios.post(`${API_BASE_URL}/admin/whatsapp/reset`, {}, { headers });
      toast.success(data.message || 'Logged out.');
      setQrData(null);
      setQrPanelOpen(true); // the new QR takes a few seconds; the panel polls for it
      fetchWaStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not log out of WhatsApp.');
    } finally {
      setLoggingOut(false);
    }
  };

  const sendTestMessage = async () => {
    if (!testPhone.trim()) { toast.error('Enter a phone number'); return; }
    try {
      setTestSending(true);
      const { data } = await axios.post(
        `${API_BASE_URL}/admin/whatsapp/test`,
        { phone: testPhone.trim(), message: testMessage.trim() || undefined },
        { headers }
      );
      if (data.success) {
        toast.success(`Test message sent to ${data.recipient || testPhone}`);
      } else {
        toast.error(data.error || 'Failed to send test message');
      }
    } catch (err) {
      const e = err.response?.data;
      toast.error(e?.not_registered ? e.error : (e?.error || 'Failed to send test message'));
    } finally {
      setTestSending(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/admin/settings?id=global`, { headers });
      if (data.success && data.settings) {
        const t = data.settings.whatsapp_templates || {};
        setWa({
          booking_confirmation: t.booking_confirmation || '',
          booking_cancellation: t.booking_cancellation || '',
          booking_reminder: t.booking_reminder || '',
          booking_caretaker: t.booking_caretaker || '',
          cancellation_caretaker: t.cancellation_caretaker || '',
          day_before_customer: t.day_before_customer || '',
          day_before_caretaker: t.day_before_caretaker || '',
          hour_before_customer: t.hour_before_customer || '',
          hour_before_caretaker: t.hour_before_caretaker || '',
        });

        setCaretakerPhone(data.settings.caretaker_phone || '');

        const r = data.settings.reminder_settings || {};
        setReminders({
          day_before: { enabled: r.day_before?.enabled ?? true, send_at: r.day_before?.send_at || '09:00' },
          hour_before: { enabled: r.hour_before?.enabled ?? true, hours_before: r.hour_before?.hours_before ?? 1 },
        });

        const e = data.settings.email_templates || {};
        setEmail({
          booking_confirmation: {
            subject: e.booking_confirmation?.subject || '',
            greeting: e.booking_confirmation?.greeting || '',
            message: e.booking_confirmation?.message || '',
          },
          booking_cancellation: {
            subject: e.booking_cancellation?.subject || '',
            message: e.booking_cancellation?.message || '',
          },
        });
      }
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const { data: current } = await axios.get(`${API_BASE_URL}/admin/settings?id=global`, { headers });
      // Only send templates that actually have content. A blank field means "leave it
      // alone" — previously it overwrote the stored template with '', which silently
      // dropped the sender back to its hardcoded default. The server strips blanks too;
      // this just avoids sending them in the first place.
      const waToSave = Object.fromEntries(Object.entries(wa).filter(([, v]) => v && v.trim()));
      const updated = {
        ...(current.settings || {}),
        whatsapp_templates: { ...(current.settings?.whatsapp_templates || {}), ...waToSave },
        email_templates: { booking_confirmation: email.booking_confirmation, booking_cancellation: email.booking_cancellation },
        reminder_settings: reminders,
        caretaker_phone: caretakerPhone.trim(),
      };
      await axios.put(`${API_BASE_URL}/admin/settings`, updated, { headers });
      toast.success('Notification templates saved!');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetWa = async (key) => {
    if (!window.confirm(`Reset this template to the system default?`)) return;
    try {
      const { data } = await axios.get(`${API_BASE_URL}/admin/settings?id=global&defaults=1`, { headers });
      const def = data.settings?.whatsapp_templates?.[key] || '';
      setWa(prev => ({ ...prev, [key]: def }));
      toast.success('Reset to default — click Save to apply');
    } catch { toast.error('Could not load defaults'); }
  };

  const resetEmail = async (key) => {
    if (!window.confirm(`Reset this email template to the system default?`)) return;
    try {
      const { data } = await axios.get(`${API_BASE_URL}/admin/settings?id=global&defaults=1`, { headers });
      const def = data.settings?.email_templates?.[key] || {};
      setEmail(prev => ({ ...prev, [key]: { ...prev[key], ...def } }));
      toast.success('Reset to default — click Save to apply');
    } catch { toast.error('Could not load defaults'); }
  };

  const resetReminderTemplate = async (key) => {
    if (!window.confirm(`Reset this reminder template to the system default?`)) return;
    try {
      const { data } = await axios.get(`${API_BASE_URL}/admin/settings?id=global&defaults=1`, { headers });
      const def = data.settings?.whatsapp_templates?.[key] || '';
      setWa(prev => ({ ...prev, [key]: def }));
      toast.success('Reset to default — click Save to apply');
    } catch { toast.error('Could not load defaults'); }
  };

  if (loading) return <div className="p-8 text-sm text-gray-500">Loading settings…</div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

      {/* Header */}
      <div className="px-8 py-6 border-b border-gray-50 bg-gray-50/30">
        <h2 className="text-xl font-bold text-gray-800">Notification Templates</h2>
        <p className="text-sm text-gray-500 mt-0.5">Customise the messages sent to customers after each booking action.</p>
      </div>

      {/* Channel tabs */}
      <div className="px-8 pt-6 flex gap-2">
        <button
          onClick={() => setChannel('whatsapp')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            channel === 'whatsapp'
              ? 'bg-[#471F21] text-white shadow-md'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          <MessageSquare className="w-4 h-4" />
          WhatsApp
        </button>
        <button
          onClick={() => setChannel('email')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            channel === 'email'
              ? 'bg-[#471F21] text-white shadow-md'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          <Mail className="w-4 h-4" />
          Email
        </button>
        <button
          onClick={() => setChannel('reminders')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            channel === 'reminders'
              ? 'bg-[#471F21] text-white shadow-md'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          <Bell className="w-4 h-4" />
          Reminders
        </button>
        <button
          onClick={() => setChannel('test')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            channel === 'test'
              ? 'bg-[#471F21] text-white shadow-md'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          <Activity className="w-4 h-4" />
          Test &amp; Status
        </button>
      </div>

      <div className="p-8 space-y-6">

        {/* ── WhatsApp tab ── */}
        {channel === 'whatsapp' && (
          <>
            <SectionCard
              title="Booking Confirmation"
              badge="sent after payment"
              onReset={() => resetWa('booking_confirmation')}
            >
              <TemplateField
                label="Message"
                value={wa.booking_confirmation}
                onChange={v => setWa(p => ({ ...p, booking_confirmation: v }))}
                rows={12}
              />
            </SectionCard>

            <SectionCard
              title="Cancellation Notice"
              badge="sent on cancellation"
              onReset={() => resetWa('booking_cancellation')}
            >
              <TemplateField
                label="Message"
                value={wa.booking_cancellation}
                onChange={v => setWa(p => ({ ...p, booking_cancellation: v }))}
                rows={5}
              />
            </SectionCard>

            <SectionCard
              title="24-Hour Reminder"
              badge="sent the day before"
              onReset={() => resetWa('booking_reminder')}
            >
              <TemplateField
                label="Message"
                value={wa.booking_reminder}
                onChange={v => setWa(p => ({ ...p, booking_reminder: v }))}
                rows={5}
              />
            </SectionCard>

            <SectionCard
              title="Caretaker — New Booking"
              badge="sent to caretaker after payment"
              onReset={() => resetWa('booking_caretaker')}
            >
              <TemplateField
                label="Message"
                value={wa.booking_caretaker}
                onChange={v => setWa(p => ({ ...p, booking_caretaker: v }))}
                rows={10}
              />
            </SectionCard>

            <SectionCard
              title="Caretaker — Cancellation"
              badge="sent to caretaker on cancellation"
              onReset={() => resetWa('cancellation_caretaker')}
            >
              <TemplateField
                label="Message"
                value={wa.cancellation_caretaker}
                onChange={v => setWa(p => ({ ...p, cancellation_caretaker: v }))}
                rows={6}
              />
            </SectionCard>

            <VariablesBar variables={WHATSAPP_VARIABLES} />
          </>
        )}

        {/* ── Email tab ── */}
        {channel === 'email' && (
          <>
            <div className="text-xs text-gray-400 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
              💡 The email layout (header image, booking details table, footer) stays the same. You're only editing the text shown to the customer.
            </div>

            <SectionCard
              title="Booking Confirmation Email"
              badge="sent after payment"
              onReset={() => resetEmail('booking_confirmation')}
            >
              <SubjectField
                value={email.booking_confirmation.subject}
                onChange={v => setEmail(p => ({ ...p, booking_confirmation: { ...p.booking_confirmation, subject: v } }))}
              />
              <TemplateField
                label="Greeting line"
                hint={"Short personal line shown in the coloured header. e.g. \"We can't wait to host you, {name}!\""}
                value={email.booking_confirmation.greeting}
                onChange={v => setEmail(p => ({ ...p, booking_confirmation: { ...p.booking_confirmation, greeting: v } }))}
                rows={2}
              />
              <TemplateField
                label="Body message"
                hint="Main paragraph shown above the booking details."
                value={email.booking_confirmation.message}
                onChange={v => setEmail(p => ({ ...p, booking_confirmation: { ...p.booking_confirmation, message: v } }))}
                rows={3}
              />
            </SectionCard>

            <SectionCard
              title="Cancellation Email"
              badge="sent on cancellation"
              onReset={() => resetEmail('booking_cancellation')}
            >
              <SubjectField
                value={email.booking_cancellation.subject}
                onChange={v => setEmail(p => ({ ...p, booking_cancellation: { ...p.booking_cancellation, subject: v } }))}
              />
              <TemplateField
                label="Body message"
                hint="What the customer reads when their booking is cancelled."
                value={email.booking_cancellation.message}
                onChange={v => setEmail(p => ({ ...p, booking_cancellation: { ...p.booking_cancellation, message: v } }))}
                rows={3}
              />
            </SectionCard>

            <VariablesBar variables={EMAIL_VARIABLES} />
          </>
        )}

        {/* ── Reminders tab ── */}
        {channel === 'reminders' && (
          <>
            {/* Caretaker recipient. Previously only settable by editing CARETAKER_PHONE
                in .env.production on the VM and recreating the containers. */}
            <div className="border border-gray-100 rounded-2xl overflow-hidden">
              <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100">
                <span className="font-semibold text-gray-800">Caretaker WhatsApp Number</span>
              </div>
              <div className="p-6 space-y-3">
                <input
                  type="tel"
                  value={caretakerPhone}
                  onChange={(e) => setCaretakerPhone(e.target.value)}
                  placeholder="+919999999999"
                  className="w-full sm:w-72 px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none font-mono"
                />
                <p className="text-xs text-gray-500 leading-relaxed">
                  Every caretaker alert and caretaker reminder goes here. Include the country
                  code (a bare 10-digit number is treated as Indian and prefixed with 91).
                  Leave blank to use the server's <code>CARETAKER_PHONE</code> setting.
                </p>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  This changes only who <strong>receives</strong> alerts. The caretaker's
                  number also appears inside the message text customers read — update that
                  separately under the <strong>WhatsApp</strong> tab.
                </p>
              </div>
            </div>

            {/* Timing config */}
            <div className="border border-gray-100 rounded-2xl overflow-hidden">
              <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100">
                <span className="font-semibold text-gray-800">Reminder Timing</span>
              </div>
              <div className="p-6 space-y-6">

                {/* Day-before */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Day-before reminder</p>
                      <p className="text-xs text-gray-400 mt-0.5">Sent to customer + caretaker the evening/morning before the booking</p>
                    </div>
                    <button
                      onClick={() => setReminders(r => ({ ...r, day_before: { ...r.day_before, enabled: !r.day_before.enabled } }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${reminders.day_before.enabled ? 'bg-[#471F21]' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${reminders.day_before.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {reminders.day_before.enabled && (
                    <div className="flex items-center gap-3">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">Send at (IST)</label>
                      <input
                        type="time"
                        value={reminders.day_before.send_at}
                        onChange={e => setReminders(r => ({ ...r, day_before: { ...r.day_before, send_at: e.target.value } }))}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:border-[#471F21] focus:ring-0 outline-none shadow-sm"
                      />
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-100" />

                {/* Hour-before */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Hour-before reminder</p>
                      <p className="text-xs text-gray-400 mt-0.5">Sent to customer + caretaker shortly before the slot starts</p>
                    </div>
                    <button
                      onClick={() => setReminders(r => ({ ...r, hour_before: { ...r.hour_before, enabled: !r.hour_before.enabled } }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${reminders.hour_before.enabled ? 'bg-[#471F21]' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${reminders.hour_before.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {reminders.hour_before.enabled && (
                    <div className="flex items-center gap-3">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">How many hours before</label>
                      <input
                        type="number"
                        min="0.5"
                        max="12"
                        step="0.5"
                        value={reminders.hour_before.hours_before}
                        onChange={e => setReminders(r => ({ ...r, hour_before: { ...r.hour_before, hours_before: parseFloat(e.target.value) || 1 } }))}
                        className="w-24 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:border-[#471F21] focus:ring-0 outline-none shadow-sm"
                      />
                      <span className="text-xs text-gray-400">hours</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Reminder message templates */}
            <SectionCard
              title="Day-before — Customer Message"
              badge="WhatsApp"
              onReset={() => resetReminderTemplate('day_before_customer')}
            >
              <TemplateField
                label="Message"
                value={wa.day_before_customer}
                onChange={v => setWa(p => ({ ...p, day_before_customer: v }))}
                rows={8}
              />
            </SectionCard>

            <SectionCard
              title="Day-before — Caretaker Alert"
              badge="WhatsApp"
              onReset={() => resetReminderTemplate('day_before_caretaker')}
            >
              <TemplateField
                label="Message"
                value={wa.day_before_caretaker}
                onChange={v => setWa(p => ({ ...p, day_before_caretaker: v }))}
                rows={6}
              />
            </SectionCard>

            <SectionCard
              title="1-Hour-before — Customer Message"
              badge="WhatsApp"
              onReset={() => resetReminderTemplate('hour_before_customer')}
            >
              <TemplateField
                label="Message"
                value={wa.hour_before_customer}
                onChange={v => setWa(p => ({ ...p, hour_before_customer: v }))}
                rows={6}
              />
            </SectionCard>

            <SectionCard
              title="1-Hour-before — Caretaker Alert"
              badge="WhatsApp"
              onReset={() => resetReminderTemplate('hour_before_caretaker')}
            >
              <TemplateField
                label="Message"
                value={wa.hour_before_caretaker}
                onChange={v => setWa(p => ({ ...p, hour_before_caretaker: v }))}
                rows={6}
              />
            </SectionCard>

            <VariablesBar variables={WHATSAPP_VARIABLES} />
          </>
        )}

        {/* ── Test & Status tab ── */}
        {channel === 'test' && (
          <>
            {/* Connection status card */}
            <div className="border border-gray-100 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 bg-gray-50/50 border-b border-gray-100">
                <span className="font-semibold text-gray-800">Connection Status</span>
                <button
                  onClick={fetchWaStatus}
                  disabled={waStatusLoading}
                  className="flex items-center gap-1.5 text-[11px] font-bold text-blue-500 hover:text-blue-700 uppercase tracking-wider disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${waStatusLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
              <div className="p-6 space-y-4">
                {waStatusLoading && !waStatus ? (
                  <p className="text-sm text-gray-400">Checking WhatsApp service…</p>
                ) : (
                  <>
                    {/* Ready badge */}
                    <div className="flex items-center gap-3">
                      {waStatus?.whatsapp_ready ? (
                        <span className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-50 text-green-700 text-sm font-semibold">
                          <CheckCircle className="w-4 h-4" /> Connected &amp; ready
                        </span>
                      ) : (
                        <span className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 text-red-700 text-sm font-semibold">
                          <XCircle className="w-4 h-4" /> Not connected
                        </span>
                      )}
                      {/* Which account is linked — catches the case where a personal
                          phone got paired instead of the business line. */}
                      {waStatus?.connected_number && (
                        <span className="text-sm text-gray-600">
                          Sending as{' '}
                          <span className="font-semibold text-gray-900">
                            +{waStatus.connected_number}
                          </span>
                          {waStatus.connected_pushname && ` (${waStatus.connected_pushname})`}
                        </span>
                      )}
                    </div>

                    {/* Pairing controls. The QR renders inline here on purpose: the
                        service's own /qr page sits behind HTTP Basic auth needing the
                        admin password, which is not to hand in a hurry. */}
                    {waStatus?.success !== false && (
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          onClick={() => setQrPanelOpen(o => !o)}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          <QrCode className="w-4 h-4" />
                          {qrPanelOpen
                            ? 'Hide QR code'
                            : waStatus?.whatsapp_ready ? 'Connect a different number' : 'Scan QR code to connect'}
                        </button>
                        {waStatus?.whatsapp_ready && (
                          <button
                            onClick={logoutWhatsApp}
                            disabled={loggingOut}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-red-200 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                          >
                            <LogOut className="w-4 h-4" />
                            {loggingOut ? 'Logging out…' : 'Log out'}
                          </button>
                        )}
                      </div>
                    )}

                    {qrPanelOpen && (
                      <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-5">
                        {qrData?.ready ? (
                          <p className="text-sm font-semibold text-green-700">
                            Connected. No QR code needed.
                          </p>
                        ) : qrData?.has_qr ? (
                          <div className="flex flex-col sm:flex-row gap-5">
                            <div className="relative">
                              <img
                                src={qrData.qr_image}
                                alt="WhatsApp pairing QR code"
                                className={`w-[220px] h-[220px] bg-white rounded-xl border border-gray-200 p-2 ${qrIsStale ? 'opacity-25' : ''}`}
                              />
                              {/* Never present a dead code as scannable — an expired scan
                                  fails silently, with nothing on the phone to say why. */}
                              {qrIsStale && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3">
                                  <RefreshCw className="w-5 h-5 animate-spin text-gray-500 mb-2" />
                                  <span className="text-xs font-semibold text-gray-700">
                                    Code expired — getting a fresh one
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 space-y-2">
                              <p className="font-semibold text-gray-800">
                                On the phone you want to send from:
                              </p>
                              <ol className="list-decimal pl-5 space-y-1">
                                <li>Open WhatsApp</li>
                                <li>Tap <strong>Settings → Linked Devices</strong></li>
                                <li>Tap <strong>Link a Device</strong></li>
                                <li>Point the camera at this code</li>
                              </ol>
                              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                                Codes expire about every 20 seconds. This one refreshes on
                                its own — scan whatever is on screen, and if it does not
                                take, wait for the next code and try again.
                              </p>
                            </div>
                          </div>
                        ) : qrData?.error ? (
                          <p className="text-sm text-red-600">{qrData.error}</p>
                        ) : (
                          <p className="text-sm text-gray-500 flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Waiting for a QR code from the WhatsApp service…
                          </p>
                        )}
                      </div>
                    )}

                    {/* Session persistence — the key diagnostic for "asks for QR every time" */}
                    {waStatus?.success !== false && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                          {waStatus?.session_persisted
                            ? <CheckCircle className="w-4 h-4 text-green-600" />
                            : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                          <span className="text-gray-600">
                            Session saved:{' '}
                            <span className="font-semibold">
                              {waStatus?.session_persisted
                                ? (waStatus.last_session_saved_at ? istDateTime(waStatus.last_session_saved_at) : 'yes')
                                : 'never — QR will keep reappearing'}
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {waStatus?.session_bucket_configured
                            ? <CheckCircle className="w-4 h-4 text-green-600" />
                            : <XCircle className="w-4 h-4 text-red-600" />}
                          <span className="text-gray-600">
                            Session storage: <span className="font-semibold">{waStatus?.session_bucket_configured ? 'configured' : 'NOT configured'}</span>
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Error surfaces */}
                    {waStatus?.last_session_save_error && (
                      <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                        <strong>Session save error:</strong> {waStatus.last_session_save_error}
                        <div className="mt-1 text-red-500">This is why the QR keeps reappearing — the session can't be saved to storage. Check the R2/S3 credentials &amp; bucket on the WhatsApp service.</div>
                      </div>
                    )}
                    {waStatus?.last_auth_error && (
                      <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                        <strong>Auth error:</strong> {waStatus.last_auth_error}
                      </div>
                    )}
                    {waStatus?.error && (
                      <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                        {waStatus.error}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Send test message */}
            <SectionCard title="Send Test Message" badge="verify delivery">
              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Phone Number</label>
                <input
                  type="tel"
                  value={testPhone}
                  onChange={e => setTestPhone(e.target.value)}
                  placeholder="e.g. 9876543210 (10-digit assumes +91)"
                  className="w-full px-5 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-medium text-gray-700 focus:border-[#471F21] focus:ring-0 outline-none transition-all shadow-sm"
                />
              </div>
              <TemplateField
                label="Message (optional)"
                hint="Leave blank to send a default test message."
                value={testMessage}
                onChange={setTestMessage}
                rows={3}
              />
              <div className="flex justify-end">
                <button
                  onClick={sendTestMessage}
                  disabled={testSending || !waStatus?.whatsapp_ready}
                  title={!waStatus?.whatsapp_ready ? 'WhatsApp is not connected' : ''}
                  className="flex items-center gap-2 px-8 py-3 bg-[#471F21] text-white font-bold rounded-xl hover:shadow-xl disabled:opacity-50 transition-all active:scale-95"
                >
                  {testSending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send Test
                </button>
              </div>
              {!waStatus?.whatsapp_ready && (
                <p className="text-xs text-amber-600">Connect WhatsApp first (scan the QR) before sending a test.</p>
              )}
            </SectionCard>
          </>
        )}

        {/* Save button — templates only, not on the test tab */}
        {channel !== 'test' && (
        <div className="flex items-center justify-end pt-4 border-t border-gray-50">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-3 px-10 py-3.5 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 hover:shadow-xl hover:shadow-green-600/20 disabled:opacity-50 transition-all transform active:scale-95"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Templates
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default SettingsNotifications;
